/**
 * Brand Token Derivation System.
 *
 * Computes all accent-dependent tokens from a single accentColor input.
 * Makes admin brand edits propagate correctly to all UI elements.
 */

import type { BrandConfig } from './brand.js';
import { parseColor, withOpacity } from '../utils/colorUtils.js';

export type ViewerThemeMode = 'dark' | 'light';

export interface BrandInputs {
  accentColor: string;
  headerBackgroundColor?: string;
  fontFamily?: string;
  companyName?: string;
  productName?: string;
  logoUrl?: string;
}

/**
 * Computes the full BrandConfig from a minimal set of admin-editable inputs.
 * All accent-derived tokens are computed from the parsed accent color.
 *
 * If accentColor is invalid/empty, returns the base config unchanged
 * (with any non-accent overrides still applied).
 */
export function computeBrandTokens(
  inputs: BrandInputs,
  base: BrandConfig,
  mode: ViewerThemeMode,
): BrandConfig {
  // Start with a deep copy of base
  const result: BrandConfig = JSON.parse(JSON.stringify(base));

  // Apply non-accent overrides
  if (inputs.companyName) result.companyName = inputs.companyName;
  if (inputs.productName) result.productName = inputs.productName;
  if (inputs.logoUrl) result.logoImageUrl = inputs.logoUrl;
  if (inputs.fontFamily) result.typography.fontFamily = inputs.fontFamily;
  if (inputs.headerBackgroundColor) result.theme.headerBackgroundColor = inputs.headerBackgroundColor;

  // Parse accent color
  const parsed = parseColor(inputs.accentColor);
  if (!parsed) {
    // Invalid/empty accent — return base with non-accent overrides only
    return result;
  }

  const { r, g, b } = parsed;
  const accent = `rgb(${r}, ${g}, ${b})`;

  // Derive accent-dependent tokens
  result.colors.accent = accent;
  result.componentDefaults.panelTitleColor = accent;
  result.componentDefaults.panelActiveColor = accent;
  result.componentDefaults.stepControlsProgressBarColor = accent;

  // Mode-dependent opacity values
  const bgOpacity = mode === 'dark' ? 0.15 : 0.22;
  const borderOpacity = mode === 'dark' ? 0.3 : 0.38;

  result.componentDefaults.toolbarActiveBackgroundColor = withOpacity(accent, bgOpacity);
  result.componentDefaults.toolbarActiveBorderColor = withOpacity(accent, borderOpacity);

  return result;
}
